import { supabase } from '../../shared/supabase/supabaseClient';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { formatArPhoneDisplay } from '@shared/phone';
import type {
  AllegedParty,
  ComplaintRow,
  ComplaintDetail,
  ComplaintHistoryEntry,
  ComplaintStatus,
} from '../domain/complaintTypes';

type CustomerProfileEmbed = { full_name: string; phone: string | null };

export type ComplaintRowDB = {
  id: string;
  order_id: string | null;
  appointment_id: string | null;
  reason: string;
  description: string;
  photo_urls: string[];
  status: string;
  alleged_party: string | null;
  kiosco_response: string | null;
  kiosco_responded_at: string | null;
  deadline_at: string;
  escalated_at: string | null;
  resolved_at: string | null;
  created_at: string;
  customer_decision_deadline_at: string | null;
  response_revision_count: number | null;
  customer_orders: {
    total_amount: number;
    status: string;
    display_code: string | null;
    pickup_type: string | null;
    customer_profiles: CustomerProfileEmbed | CustomerProfileEmbed[] | null;
  } | null;
  appointments: {
    starts_at: string;
    status: string;
    customer_name: string | null;
    customer_phone: string | null;
  } | null;
};

export function embedProfile(
  profiles: CustomerProfileEmbed | CustomerProfileEmbed[] | null | undefined,
): CustomerProfileEmbed | null {
  if (!profiles) return null;
  return Array.isArray(profiles) ? (profiles[0] ?? null) : profiles;
}

export function mapRow(row: ComplaintRowDB): ComplaintRow {
  const order = row.customer_orders;
  const appt = row.appointments;
  const isAppt = Boolean(row.appointment_id);
  const profile = embedProfile(order?.customer_profiles);
  const displayCode = order?.display_code
    ? String(order.display_code).toUpperCase()
    : row.order_id
      ? row.order_id.slice(0, 8).toUpperCase()
      : null;
  // Fallback con identidad: si no hay nombre, el teléfono distingue al cliente
  // mejor que un genérico "Cliente" (UX audit #1467).
  const rawPhone = isAppt ? appt?.customer_phone : profile?.phone;
  const phoneAsName = rawPhone ? formatArPhoneDisplay(rawPhone) : '';
  return {
    id: row.id,
    order_id: row.order_id,
    appointment_id: row.appointment_id,
    reason: row.reason as ComplaintRow['reason'],
    description: row.description,
    photo_urls: row.photo_urls ?? [],
    status: row.status as ComplaintStatus,
    alleged_party: (row.alleged_party as AllegedParty) || 'merchant',
    kiosco_response: row.kiosco_response,
    kiosco_responded_at: row.kiosco_responded_at,
    deadline_at: row.deadline_at,
    escalated_at: row.escalated_at,
    resolved_at: row.resolved_at,
    created_at: row.created_at,
    customer_name: isAppt
      ? (appt?.customer_name?.trim() || phoneAsName || 'Cliente sin nombre')
      : (profile?.full_name?.trim() || phoneAsName || 'Cliente sin nombre'),
    customer_phone: isAppt
      ? (appt?.customer_phone ?? null)
      : (profile?.phone ?? null),
    order_total: isAppt ? 0 : Number(order?.total_amount ?? 0),
    order_status: isAppt ? (appt?.status ?? 'unknown') : (order?.status ?? 'unknown'),
    display_code: isAppt ? null : displayCode,
    pickup_type: isAppt ? null : (order?.pickup_type ?? null),
    subject_kind: isAppt ? 'appointment' : 'order',
    appointment_label: isAppt
      ? (appt?.starts_at
          ? `Turno · ${formatLocalDateTime(appt.starts_at)}`
          : 'Turno')
      : null,
    response_revision_count: Number(row.response_revision_count ?? 0),
    customer_decision_deadline_at: row.customer_decision_deadline_at ?? null,
  };
}

const SELECT_COLS =
  'id, order_id, appointment_id, reason, description, photo_urls, status, alleged_party, kiosco_response, kiosco_responded_at, deadline_at, escalated_at, resolved_at, created_at, customer_decision_deadline_at, response_revision_count, customer_orders(total_amount, status, display_code, pickup_type, customer_profiles(full_name, phone)), appointments(starts_at, status, customer_name, customer_phone)';

const COMPLAINTS_PAGE_SIZE = 50;

export async function fetchComplaintsByOrganization(
  orgId: string,
  statusFilter?: ComplaintStatus[],
  page = 0,
): Promise<ComplaintRow[]> {
  let query = supabase
    .from('customer_complaints')
    .select(SELECT_COLS)
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })
    .range(page * COMPLAINTS_PAGE_SIZE, (page + 1) * COMPLAINTS_PAGE_SIZE - 1);

  if (statusFilter && statusFilter.length > 0) {
    query = query.in('status', statusFilter);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data as unknown as ComplaintRowDB[]).map(mapRow);
}

/** Whether there are more complaints to load (checks if the next page has rows). */
export async function hasMoreComplaints(
  orgId: string,
  statusFilter: ComplaintStatus[] | undefined,
  loadedCount: number,
): Promise<boolean> {
  let query = supabase
    .from('customer_complaints')
    .select('id')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })
    .range(loadedCount, loadedCount);
  if (statusFilter && statusFilter.length > 0) {
    query = query.in('status', statusFilter);
  }
  const { data, error } = await query;
  if (error) return false;
  return (data?.length ?? 0) > 0;
}

/** Open complaints that need merchant response (segment / inbox badge). */
export async function countOpenComplaintsForOrg(orgId: string): Promise<number> {
  const { count, error } = await supabase
    .from('customer_complaints')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .in('status', ['open']);
  if (error) throw new Error(error.message || `countOpenComplaintsForOrg failed (code ${error.code ?? 'unknown'})`);
  return count ?? 0;
}

export async function fetchComplaintById(complaintId: string): Promise<ComplaintDetail | null> {
  const { data: complaint, error } = await supabase
    .from('customer_complaints')
    .select(SELECT_COLS)
    .eq('id', complaintId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!complaint) return null;

  const row = complaint as unknown as ComplaintRowDB;
  let items: ComplaintDetail['items'] = [];
  if (row.order_id) {
    const { data: orderItems, error: itemsError } = await supabase
      .from('customer_order_items')
      .select('product_name, quantity, unit_price, options, line_note')
      .eq('order_id', row.order_id);
    if (itemsError) {
      console.warn('[fetchComplaintById] order items', itemsError.message);
    }
    items = ((orderItems ?? []) as Array<{
      product_name: string;
      quantity: number;
      unit_price: number;
      options?: unknown;
      line_note?: string | null;
    }>).map((item) => ({
      product_name: item.product_name,
      quantity: Number(item.quantity),
      unit_price: Number(item.unit_price),
      options: Array.isArray(item.options) ? item.options : [],
      line_note: item.line_note?.trim() ? item.line_note.trim() : null,
    }));
  }

  return {
    ...mapRow(row),
    items,
  };
}

/** Códigos internos de `merchant_respond_complaint` → copy user-facing. */
export const RESPOND_ERROR_USER_MESSAGES = {
  response_too_short: 'Escribí al menos 5 caracteres.',
  revision_limit: 'Ya reenviaste una vez. Esperá al cliente.',
  invalid_status: 'Este reclamo ya no se puede responder.',
  already_decided: 'El cliente ya decidió.',
  not_authenticated: 'Tenés que iniciar sesión.',
  organization_required: 'Tenés que iniciar sesión.',
  forbidden: 'No podés responder reclamos.',
  module_permission_denied: 'No podés responder reclamos.',
  complaint_not_found: 'Este reclamo ya no está.',
} as const;

export function mapRespondError(message: string): string {
  const m = message.toLowerCase();
  for (const [code, friendly] of Object.entries(RESPOND_ERROR_USER_MESSAGES)) {
    if (m.includes(code.toLowerCase())) return friendly;
  }
  return message;
}

/** True si el mensaje ya es copy esperado o contiene un código interno mapeado. */
export function isExpectedRespondError(message: string): boolean {
  const values: string[] = Object.values(RESPOND_ERROR_USER_MESSAGES);
  if (values.includes(message)) return true;
  const m = message.toLowerCase();
  return Object.keys(RESPOND_ERROR_USER_MESSAGES).some((code) => m.includes(code.toLowerCase()));
}

/** Responde y propone cierre → pending_customer (RPC). */
export async function respondComplaint(complaintId: string, response: string): Promise<void> {
  const { error } = await supabase.rpc('merchant_respond_complaint', {
    p_complaint_id: complaintId,
    p_response: response,
  });
  if (error) throw new Error(mapRespondError(error.message));
}

/** Fetches the status history timeline for a complaint. */
export async function fetchComplaintHistory(
  complaintId: string,
  orgId?: string | null,
): Promise<ComplaintHistoryEntry[]> {
  let query = supabase
    .from('complaint_status_history')
    .select('id, event_type, from_status, to_status, actor_kind, metadata, created_at')
    .eq('complaint_id', complaintId);
  // #798: filtrar por organization_id además de complaint_id
  if (orgId) query = query.eq('organization_id', orgId);
  const { data, error } = await query.order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ComplaintHistoryEntry[];
}
